import React, { useEffect, useState } from "react";
import {
  Table,
  Popconfirm,
  Select,
  Input,
  Form,
  message,
} from "antd";
import moment from "moment";
import { platformApi } from "../../../api";

const { Option } = Select;

const issueTypes = [
  { key: "FOC", title: "FOC" },
  { key: "PAID", title: "PAID" },
  { key: "WARRANTY", title: "WARRANTY" },
];

const makeEmptyRow = () => ({
  id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  jobCode: null,
  issueType: "PAID",
  issueDate: moment().toISOString(),
  mrp: 0,
});

/** Resolve Job Code charge for a VehicleMaster id (same rule as Job Invoice). */
const resolveJobCodeMrp = (jc, vehicleMasterId) => {
  if (!jc) return 0;
  const prices = Array.isArray(jc.vehicleModel) ? jc.vehicleModel : [];
  if (vehicleMasterId && prices.length) {
    const matched = prices.filter(
      (item) => item.vehicle && item.vehicle.id === vehicleMasterId
    );
    if (matched.length > 0) {
      return parseFloat(matched[0].price) || 0;
    }
    return 0;
  }
  return 0;
};

function LabourTable({
  dataSource = [],
  onChange,
  editable = true,
  vehicleMasterId = null,
}) {
  const [jobCodes, setJobCodes] = useState([]);

  useEffect(() => {
    platformApi
      .get("/api/jobCode/")
      .then((res) => {
        const list =
          res?.data?.response?.data ||
          res?.data?.data ||
          res?.data?.response ||
          [];
        const arr = Array.isArray(list) ? list : list.jobCodes || [];
        setJobCodes(arr.filter((j) => j && j.id && j.code));
      })
      .catch((err) => {
        console.error("Failed to load job codes", err);
        message.error("Unable to fetch labour/consumable codes");
      });
  }, []);

  // Keep one blank trailing row while editing so the next line appears automatically
  useEffect(() => {
    if (!editable || !onChange) return;
    if (!dataSource.length) {
      onChange([makeEmptyRow()]);
      return;
    }
    const last = dataSource[dataSource.length - 1];
    if (last?.jobCode?.id) {
      onChange([...dataSource, makeEmptyRow()]);
    }
  }, [editable, dataSource, onChange]);

  const updateRow = (index, patch) => {
    let next = [...dataSource];
    next[index] = { ...next[index], ...patch };
    // Selecting labour on the last row → extend with a new empty row
    if (editable && patch.jobCode?.id) {
      const last = next[next.length - 1];
      if (last?.jobCode?.id) {
        next = [...next, makeEmptyRow()];
      }
    }
    onChange && onChange(next);
  };

  const removeRow = (index) => {
    const next = dataSource.filter((_, i) => i !== index);
    const ensured =
      next.length === 0
        ? [makeEmptyRow()]
        : editable && next[next.length - 1]?.jobCode?.id
          ? [...next, makeEmptyRow()]
          : next;
    onChange && onChange(ensured);
  };

  // Form convention: editable=true => can edit; locked when viewing
  const locked = !editable;

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: 60,
      render: (_t, _r, index) => index + 1,
    },
    {
      title: "Labour details",
      dataIndex: "jobCode",
      key: "jobCode",
      width: 420,
      render: (_t, record, index) => (
        <Form.Item>
          <Select
            dropdownStyle={{ overflowY: "auto", overflowX: "hidden" }}
            showSearch
            showAction={["click", "focus"]}
            style={{ width: "100%" }}
            placeholder="Select labour / consumable"
            disabled={locked}
            value={record.jobCode?.id}
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option.props.children)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            onChange={(id) => {
              const jc = jobCodes.find((j) => j.id === id);
              const mrp = resolveJobCodeMrp(jc, vehicleMasterId);
              updateRow(index, {
                jobCode: jc
                  ? {
                      id: jc.id,
                      code: jc.code,
                      description: jc.description,
                      consumable: jc.consumable,
                    }
                  : null,
                mrp,
              });
            }}
          >
            {record.jobCode?.id &&
              !jobCodes.some((j) => j.id === record.jobCode.id) && (
                <Option key={record.jobCode.id} value={record.jobCode.id}>
                  {record.jobCode.code
                    ? `${record.jobCode.code} - ${
                        record.jobCode.description || ""
                      }`
                    : record.jobCode.displayLabel || record.jobCode.id}
                </Option>
              )}
            {jobCodes.map((jc) => (
              <Option key={jc.id} value={jc.id}>
                {jc.code} - {jc.description}
                {jc.consumable ? " (Consumable)" : ""}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Issue Type",
      dataIndex: "issueType",
      key: "issueType",
      width: 125,
      render: (_t, record, index) => (
        <Form.Item>
          <Select
            dropdownStyle={{ overflowY: "auto", overflowX: "hidden" }}
            showSearch
            disabled={locked}
            placeholder="Issue Type"
            value={record.issueType || "PAID"}
            onChange={(val) => updateRow(index, { issueType: val })}
          >
            {issueTypes.map((issue) => (
              <Option key={issue.key} value={issue.title}>
                {issue.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: "Issue Date",
      dataIndex: "issueDate",
      key: "issueDate",
      width: 110,
      render: (_t, record) => (
        <span>
          {record.issueDate
            ? moment(record.issueDate).format("DD-MM-YYYY")
            : moment().format("DD-MM-YYYY")}
        </span>
      ),
    },
    {
      title: "MRP",
      dataIndex: "mrp",
      key: "mrp",
      width: 100,
      render: (_t, record, index) => (
        <Form.Item>
          <Input
            disabled={locked}
            placeholder="Mrp"
            value={record.mrp}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9.]/g, "");
              const parts = value.split(".");
              const cleaned =
                parts[0] + (parts.length > 1 ? "." + parts[1] : "");
              updateRow(index, { mrp: cleaned });
            }}
          />
        </Form.Item>
      ),
    },
    {
      title: "Action",
      dataIndex: "remove",
      key: "x",
      width: 80,
      render: (_t, record, index) => {
        // Hide delete on the trailing empty row
        const isTrailingEmpty =
          !record.jobCode?.id && index === dataSource.length - 1;
        return (
          <Form.Item>
            {locked || isTrailingEmpty ? null : (
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => removeRow(index)}
              >
                <a style={{ color: "#6c7b8a" }}>Delete</a>
              </Popconfirm>
            )}
          </Form.Item>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        className="parts-invoice-table"
        childrenColumnName="none"
        rowKey={(r) => r.id || r.key}
        dataSource={dataSource}
        columns={columns}
        tableLayout="fixed"
        pagination={false}
        style={{ marginTop: "1rem", marginBottom: "1rem" }}
      />
    </div>
  );
}

export default LabourTable;
